import {
  DirectionRight,
  NormalArrowFollowFill,
  PromptQuestionCircleFill,
} from "@/Icons";
import { GoalTypeEnums, QueryHomeInfoResult } from "@/api/home/interface";
import { Divider, Progress, Radio, Tooltip } from "@ad/anq-ui";
import React, { useState } from "react";
import { getSum, goalTypeList } from "../common";
import DrawerGoalConfig from "../components/drawer_goal_config";
import { renderCentPrice } from "@/common/render";
import moment from "moment";
import numeral from "numeral";

enum GoalTimeEnums {
  /** 年目标 */
  YEAR = "year",
  MONTH = "month",
}

interface Props {
  homeInfo: QueryHomeInfoResult;
  onRefresh: () => void;
}

/** 经营概况及目标 */
const OverviewTarget = ({ homeInfo, onRefresh }: Props) => {
  const [currentTarget, setCurrentTarget] = useState(GoalTimeEnums.YEAR);
  const [drawerConfigVisible, setDrawerConfigVisible] = useState(false);

  /** 获取支付或者花费的进度值. 单位: 分 */
  const getGoalProgress = (type: GoalTypeEnums) => {
    if (currentTarget === GoalTimeEnums.YEAR) {
      return getSum(homeInfo?.[`${type}GoalPerMonth`] || []);
    }

    return homeInfo?.[`${type}GoalPerMonth`]?.[moment().get("month")];
  };

  const getCurrentProgress = (type: GoalTypeEnums) => {
    if (currentTarget === GoalTimeEnums.YEAR) {
      if (type === GoalTypeEnums.GMV) {
        return homeInfo?.yearGmv;
      }

      return homeInfo?.yearCost;
    }

    if (type === GoalTypeEnums.GMV) {
      return homeInfo?.monthGmv;
    }

    return homeInfo?.monthCost;
  };

  const getTimePercent = () => {
    let ret = 0;

    if (currentTarget === GoalTimeEnums.YEAR) {
      ret =
        moment().diff(moment().startOf("year"), "days") /
        moment().endOf("year").diff(moment().startOf("year"), "days");
    } else {
      ret =
        moment().diff(moment().startOf("month"), "days") /
        moment().endOf("month").diff(moment().startOf("month"), "days");
    }

    return numeral(ret).format("0.00%");
  };

  /** 渲染进度条上面的百分比数字 */
  const renderPercent = (type) => {
    let ret = getCurrentProgress(type) / getGoalProgress(type);

    if (ret > 1) {
      ret = 1;
    }

    if (ret > 0.5) {
      return <span className="text-white">{numeral(ret).format("0.00%")}</span>;
    }

    return (
      <span className="text-text-black/[.35]">
        {numeral(ret).format("0.00%")}
      </span>
    );
  };

  /** 渲染进度条 */
  const renderProgress = (item) => {
    const noConfigGoal = !homeInfo?.[`${item.type}GoalPerMonth`]?.filter(
      (item) => item
    ).length;

    return noConfigGoal ? (
      <div
        className="dmp-link flex items-center text-sm text-text-blue"
        onClick={() => setDrawerConfigVisible(true)}
      >
        请先设置目标
        <DirectionRight className="ml-1" />
      </div>
    ) : (
      <>
        <Progress
          strokeColor="#4C67FF"
          className="w-[196px]"
          style={{ width: 196 }}
          showInfo={false}
          percent={
            (getCurrentProgress(item.type) / getGoalProgress(item.type)) * 100
          }
          strokeWidth={14}
        />
        <div className="absolute left-0 top-0 h-[22px] w-[196px] text-center text-xs">
          {renderPercent(item.type)}
        </div>
      </>
    );
  };

  const renderProgressValue = (item) => {
    const noConfigGoal = !homeInfo?.[`${item.type}GoalPerMonth`]?.length;

    return noConfigGoal ? null : (
      <>（共 {renderCentPrice(getGoalProgress(item.type))} 元）</>
    );
  };

  const handleShowConfig = () => {
    setDrawerConfigVisible(true);
  };

  return (
    <div className="w-[984px] rounded-lg bg-white bg-[url(https://commimg.pddpic.com/upload/dmp/home_bg.png.slim.png)] bg-cover bg-no-repeat p-6">
      <div className="title mb-6 overflow-hidden">
        <span className="float-left text-base font-semibold  leading-[32px] text-text-black/[.95]">
          经营概况及目标
        </span>
        <Radio.Group
          className="float-left ml-4"
          value={currentTarget}
          onChange={(e) => setCurrentTarget(e.target.value)}
        >
          <Radio.Button value={GoalTimeEnums.YEAR}>年目标</Radio.Button>
          <Radio.Button value={GoalTimeEnums.MONTH}>月目标</Radio.Button>
        </Radio.Group>
        <div className="float-right leading-[32px] text-text-black/[.65]">
          <span
            className="dmp-link float-right flex cursor-pointer items-center"
            onClick={handleShowConfig}
          >
            <NormalArrowFollowFill className="mr-1 text-[16px]" />
            目标配置
          </span>
        </div>
      </div>

      <div className="flex gap-6">
        {goalTypeList.map((item) => (
          <div key={item.title} className="flex-1">
            <div className="title flex items-center leading-[22px]">
              <span className="text-sm font-semibold">{item.title}</span>
              <Tooltip title={item.tooltip} arrowPointAtCenter>
                <PromptQuestionCircleFill className="ml-1 mr-2 text-text-black/[.35]" />
              </Tooltip>
              <span className="text-xs text-text-black/[.5]">
                {GoalTimeEnums.YEAR === currentTarget
                  ? "年度至今累计"
                  : "月初至今累计"}
              </span>
            </div>

            <div className="money mt-2 font-semibold">
              <span className="text-xl text-text-black-base ">
                {renderCentPrice(
                  homeInfo?.[
                    `${currentTarget}${item.type
                      .slice(0, 1)
                      .toUpperCase()}${item.type.slice(1)}`
                  ]
                )}
              </span>
              <span className="ml-[6px]">元</span>
            </div>
            <div className="my-4 h-[1px] w-[24px] bg-[#EBECF2]" />

            <div className="progress grid grid-cols-[auto_1px_1fr] items-center gap-[8px_12px]">
              <div className="text-text-black/50">
                目标进度{renderProgressValue(item)}
              </div>
              <Divider type="vertical" className="h-[14px] border-[#CECFD6]" />
              <div className="text-text-black/50">时间进度</div>
              <div className="relative">{renderProgress(item)}</div>
              <div />
              <div className="">{getTimePercent()}</div>
            </div>
          </div>
        ))}
      </div>
      <DrawerGoalConfig
        visible={drawerConfigVisible}
        onClose={() => setDrawerConfigVisible(false)}
        homeInfo={homeInfo}
        onRefresh={onRefresh}
      />
      <style>
        {`
        .anq-progress-inner {
          border-radius: 7px!important;
          transform: translateX(-2px)!important;
        }
        `}
      </style>
    </div>
  );
};

export default OverviewTarget;
